import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom"; // <-- import useParams
import { useQuery, useMutation } from "@apollo/client";

import {
  PaymentOption,
  PriceSettingRow,
  ProfileLevels,
  UserData,
} from "./types";
import AppsTab from "./AppsTab";
import TransactionsTab, { TransactionData } from "./TransactionsTab";
import InvoicesTab from "./InvoicesTab";
import PriceSettingsTab from "./PriceSettingsTab";
import { Button, Tabs, TabsProps, Spin, Alert, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import CustomSelect from "../../CustomSelect";
import ActivityLogTab from "./ActivityLogTab";
import BalanceModal from "./BalanceModal/BalanceModal";
import { login } from "../../../utils/user";
import LogoAvatar from "../../../assets/logoAvatar.svg";
import Page from "../../Page";
import styles from "./ViewUser.module.scss";
import UserModerationToggle from "../../Moderation/UserModerationToggle";

import {
  ADD_BALANCE,
  CHANGE_USER_STATUS,
  REMOVE_BALANCE,
  GET_USER_BY_ID,
  GET_USER_INVOICES_BY_ID, // make sure to import your query
} from "../../../api/graphql/users";

const sampleTransactions: TransactionData[] = [
  {
    depositDate: new Date(),
    paymentOption: "Credit Card",
    idNumber: "001",
    depositAmount: 100.5,
  },
];

// The levels an admin hands out, by the backend's ProfileLevels names, in
// tier order.
export const statusOptions = [
  { value: "STARTER", label: "Starter" },
  { value: "ADVANCED", label: "Advanced" },
  { value: "EXPERT", label: "Expert" },
  { value: "CUSTOM", label: "Custom" },
];

export const invoiceOptions = {
  items: [
    { key: PaymentOption.INVOICE, label: "Invoice" },
    { key: PaymentOption.BONUS, label: "Bonus" },
  ],
};

const ViewUser: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // get userId from URL

  const { data, loading, error, refetch } = useQuery(GET_USER_BY_ID, {
    variables: { userId: Number(id) },
    fetchPolicy: "network-only",
  });

  const {data: invoices, loading: invoicesLoading } = useQuery(GET_USER_INVOICES_BY_ID, {variables: {userId: Number(id)}})

  const user: UserData | undefined = data?.getUserById;

  const [savingLevel, setSavingLevel] = useState(false);
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState<
    "add" | "remove" | null
  >(null);
  const navigate = useNavigate();

  // Handle mutations (same as before)
  // const [changeUserStatus] = useMutation(CHANGE_USER_STATUS);
  // const [removeBalance] = useMutation(REMOVE_BALANCE);

  // const handleSaveUserStatus = async (value: string) => {
  //   if (!user) return;

  //   const numIndex = Number(value);
  //   try {
  //     await changeUserStatus({
  //       variables: {
  //         userId: user.id,
  //         userStatus: ProfileLevels[numIndex],
  //       },
  //       optimisticResponse: {
  //         changeUserStatus: {
  //           __typename: "User",
  //           id: user.id,
  //           profileLevel: ProfileLevels[numIndex],
  //         },
  //       },
  //     });
  //     await refetch();
  //     setIsStatusModalOpen(false);
  //   } catch (error) {
  //     console.error("Error updating user status:", error);
  //   }
  // };

  // const handleSaveBalance = async (value: {
  //   balance: number;
  //   invoiceType: PaymentOption;
  // }) => {
  //   if (!user) return;

  //   const variables = {
  //     userId: user.id,
  //     paymentOption: PaymentOption[value.invoiceType],
  //     sum: value.balance,
  //   };

  //   try {
  //     if (isBalanceModalOpen === "add") {
  //       refetch().then((data: any) => console.log("refetch", data));
  //     } else if (isBalanceModalOpen === "remove") {
  //       await removeBalance({
  //         variables,
  //         optimisticResponse: {
  //           removeBalance: {
  //             __typename: "User",
  //             id: user.id,
  //             balance: user.balance - value.balance,
  //           },
  //         },
  //       });
  //     }
  //     await refetch();
  //     setIsBalanceModalOpen(null);
  //   } catch (error) {
  //     console.error("Error updating balance:", error);
  //   }
  // };

  const onBack = () => {
    navigate("/users");
  };
 
  const [changeUserStatus] = useMutation(CHANGE_USER_STATUS, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          user(existingUser = {}) {
            return {
              ...existingUser,
              ...data.changeUserStatus,
            };
          },
        },
      });
    },
  });

  const [addBalance] = useMutation(ADD_BALANCE, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          user(existingUser = {}) {
            return {
              ...existingUser,
              ...data.addBalance,
            };
          },
        },
      });
    },
  });

  const [removeBalance] = useMutation(REMOVE_BALANCE, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          user(existingUser = {}) {
            return {
              ...existingUser,
              ...data.removeBalance,
            };
          },
        },
      });
    },
  });

  useEffect(() => {
    if (user) console.log(user, "user");
  }, [user]);

  if (loading || invoicesLoading)
    return (
      <Page>
        <Spin className={styles.loading} tip="Loading user...">
          <div />
        </Spin>
      </Page>
    );
  if (error)
    return (
      <Alert
        message="Error loading user"
        description={error.message}
        type="error"
      />
    );

  if (!user) return <Alert message="User not found" type="warning" />;

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Apps",
      children: <AppsTab userId={user.id} apps={user.connectedApps || []} />,
    },
    {
      key: "2",
      label: "Transactions",
      children: (
        <TransactionsTab transactions={user.payments || sampleTransactions} />
      ),
    },
    {
      key: "3",
      label: "Invoices",
      children: (
        <InvoicesTab
          invoices={
            invoices?.getUserInvoicesById
          }
        />
      ),
    },
    {
      key: "4",
      label: "Price settings",
      children: (
        <PriceSettingsTab
          // The tab copies its prices into state once: a new plan has to
          // remount it, or it keeps showing the old plan's prices.
          key={user.profileLevel}
          refetch={refetch}
          userId={user.id}
          priceSettings={{...user.userPricing, LIMITS: {apps: user.applicationLimit, keywords: user.keywordLimit}} as PriceSettingRow}
          profileLevels={user.profileLevel}
        />
      ),
    },
    {
      key: "5",
      label: "Activity log",
      children: <ActivityLogTab userId={Number(user.id)} />,
    },
  ];

  // A level outside them (Trial, Pro, set elsewhere) still shows as the
  // current one.
  const levelOptions = [...statusOptions];
  // profileLevel comes as the GraphQL name ("ADVANCED"), not the enum number.
  if (
    !levelOptions.some((option) => option.value === String(user.profileLevel))
  ) {
    const level = String(user.profileLevel);
    levelOptions.push({
      value: level,
      label: level.charAt(0) + level.slice(1).toLowerCase(),
    });
  }

  // Saved as soon as it is picked, no confirmation.
  const handleChangeLevel = async (level: string) => {
    if (level === String(user.profileLevel)) return;
    setSavingLevel(true);
    try {
      await changeUserStatus({
        variables: { userId: user.id, userStatus: level },
      });
      await refetch();
      const label = levelOptions.find((o) => o.value === level)?.label;
      message.success(`Status changed to ${label ?? level}`);
    } catch (error: any) {
      message.error(error?.message || "The status was not changed");
    } finally {
      setSavingLevel(false);
    }
  };

  const handleSaveBalance = async (value: {
    balance: number;
    invoiceType: PaymentOption;
  }) => {
    const { id } = user;
    const variables = {
      userId: id,
      paymentOption: PaymentOption[value.invoiceType],
      sum: value.balance,
    };

    try {
      if (isBalanceModalOpen === "add") {
        // await addBalance({
        //   variables,
        //   optimisticResponse: {
        //     addBalance: {
        //       __typename: "User",
        //       id: id,
        //       balance: user.balance + value.balance,
        //     },
        //   },
        // });
        refetch();
      } else if (isBalanceModalOpen === "remove") {
        await removeBalance({
          variables,
          optimisticResponse: {
            removeBalance: {
              __typename: "User",
              id: id,
              balance: user.balance - value.balance,
            },
          },
        });
      }
      await refetch();
      setIsBalanceModalOpen(null);
    } catch (error) {
      console.error("Error updating balance:", error);
    }
  };

  // useEffect(() => {
  //   console.log(user, "user");
  // });

  return (
    <Page
      extra={
        <button type="button" className={styles.back} onClick={onBack}>
          <ArrowLeftOutlined />
          Back to users
        </button>
      }
    >
      <div className={styles.header}>
        <div className={styles.identity}>
          <img src={LogoAvatar} alt={user.name} className={styles.avatar} />
          <div className={styles.identityText}>
            <h1 className={styles.name}>{user.name}</h1>
            <span className={styles.email}>{user.email}</span>
            <span className={styles.meta}>
              ID: {user.id}
              {user.phone && <> · Phone: {user.phone}</>}
              {user.telegram && <> · Telegram: {user.telegram}</>}
            </span>
          </div>
        </div>

        <div className={styles.actions}>
          <Button type="primary" onClick={() => setIsBalanceModalOpen("add")}>
            Add Balance
          </Button>
          <Button className={styles.outlined} onClick={() => login(user.id)}>
            Login
          </Button>
        </div>
      </div>

      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Status</span>
          <CustomSelect
            className={styles.levelSelect}
            aria-label="Status"
            value={String(user.profileLevel)}
            options={levelOptions}
            disabled={savingLevel}
            onChange={handleChangeLevel}
          />
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Balance</span>
          <span className={styles.statValue}>
            $ {Number(user.balance).toFixed(2)}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Spendings</span>
          <span className={styles.statValue}>
            $ {Number(user.totalSpendings).toFixed(2)}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Manual moderation</span>
          <UserModerationToggle userId={user.id} />
        </div>
      </div>

      <Tabs defaultActiveKey="1" items={items} />
      <BalanceModal
        userId={user.id}
        isOpen={isBalanceModalOpen !== null}
        onClose={() => setIsBalanceModalOpen(null)}
        title={isBalanceModalOpen === "add" ? "Add Balance" : "Remove Balance"}
        onSave={handleSaveBalance}
        onCancel={() => setIsBalanceModalOpen(null)}
        onOk={() => setIsBalanceModalOpen(null)}
        balance={user.balance}
      />
    </Page>
  );
};

export default ViewUser;
